import { useState } from 'react';
import postResolvePendingProspect, {
  ResolveDecision,
} from '../services/postResolvePendingProspect';

interface AlertMessage {
  description: string;
  variant: 'error' | 'success';
}

interface Status {
  loading: boolean;
  alertMessage: AlertMessage;
  success: boolean;
  error: boolean;
}

export type PendingProspectActions = 'APPROVE' | 'REJECT';

export interface SelectedPendingProspect {
  id: string;
  companyName: string;
  usersCount: number;
  action: PendingProspectActions;
}

type MessageType = {
  [key: string]: AlertMessage;
};

const ACTION_TO_DECISION: Record<PendingProspectActions, ResolveDecision> = {
  APPROVE: 'APPROVED',
  REJECT: 'REJECTED',
};

/**
 * Approve / reject pending prospect registration state hook
 * @param handleRefresh Function to refresh the list
 */
const useResolvePendingProspect = (handleRefresh: () => void) => {
  const MESSAGES: MessageType = {
    ERROR_GENERIC: {
      description: 'COMMON:ERRORS:IT_ERROR',
      variant: 'error',
    },
    SUCCESS_APPROVED: {
      description: 'PENDING_PROSPECTS:ALERT_MESSAGES:APPROVED',
      variant: 'success',
    },
    SUCCESS_REJECTED: {
      description: 'PENDING_PROSPECTS:ALERT_MESSAGES:REJECTED',
      variant: 'success',
    },
    ERROR_APPROVED: {
      description: 'PENDING_PROSPECTS:ALERT_MESSAGES:APPROVED_ERROR',
      variant: 'error',
    },
    ERROR_REJECTED: {
      description: 'PENDING_PROSPECTS:ALERT_MESSAGES:REJECTED_ERROR',
      variant: 'error',
    },
  } as const;

  const [status, setStatus] = useState<Status>({
    loading: false,
    success: false,
    error: false,
    alertMessage: MESSAGES.ERROR_GENERIC,
  });

  const [selectedProspect, setSelectedProspect] = useState<SelectedPendingProspect>({
    id: '',
    companyName: '',
    usersCount: 0,
    action: 'APPROVE',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Function to set the error state.
   * @param {boolean} error - The error state.
   */
  const setIsError = (error: boolean) => {
    setStatus((prevState) => ({ ...prevState, error }));
  };

  /**
   * Function to set the success state.
   * @param {boolean} success - The success state.
   */
  const setIsSuccess = (success: boolean) => {
    setStatus((prevState) => ({ ...prevState, success }));
  };

  /**
   * Function to set the success/error state with message.
   * @param success - True if success.
   * @param message - The message.
   */
  const setResponseMessage = (success: boolean, message: AlertMessage) => {
    setStatus({
      loading: false,
      error: !success,
      success,
      alertMessage: message,
    });
  };

  /**
   * Resolves a pending prospect with APPROVED or REJECTED decision
   * @async
   * @param body - body payload request
   * @param body.id - pending prospect id
   * @param body.decision - APPROVED or REJECTED
   */
  async function resolveProspect(body: { id: string; decision: ResolveDecision }) {
    try {
      setStatus((prevState) => ({ ...prevState, loading: true }));
      await postResolvePendingProspect(body);
      setTimeout(() => {
        handleRefresh();
        setResponseMessage(
          true,
          body.decision === 'APPROVED' ? MESSAGES.SUCCESS_APPROVED : MESSAGES.SUCCESS_REJECTED
        );
      }, 1000);
    } catch (error) {
      setResponseMessage(
        false,
        body.decision === 'APPROVED' ? MESSAGES.ERROR_APPROVED : MESSAGES.ERROR_REJECTED
      );
    } finally {
      setIsModalOpen(false);
    }
  }

  /**
   * Handle submit
   */
  const handleSubmit = () => {
    resolveProspect({
      id: selectedProspect.id,
      decision: ACTION_TO_DECISION[selectedProspect.action],
    });
  };

  /**
   * On click action buttons
   * @param prospect Selected pending prospect
   */
  const onActionClick = (prospect: SelectedPendingProspect) => {
    setIsModalOpen(true);
    setSelectedProspect(prospect);
  };

  const { loading: isLoading, success: isSuccess, error: isError, alertMessage } = status;

  return {
    isLoading,
    isSuccess,
    isError,
    alertMessage,
    setIsSuccess,
    setIsError,
    handleSubmit,
    isModalOpen,
    setIsModalOpen,
    selectedProspect,
    setSelectedProspect,
    onActionClick,
  };
};

export default useResolvePendingProspect;
